import React, { useEffect, useState, useContext, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import '../../components/editor/EditorToolbar.css';
import { getPostById, deletePost, getBoardCodeById } from '../../api/boardApi';
import AiContentToolbar from '../../components/TdxAi/AiContentToolbar';
import CommentList from '../../components/Comment/CommentList';
import FileAttachment from '../../components/FileAttachment/FileAttachment';
import TodoRegisterLayer from '../../components/todo/TodoRegisterLayer';
import { TODO_REF_TYPE, TODO_REF_TABLE } from '../../api/todoApi';
import '../../css/Ui.css';

function ViewPost({ collapsed, id: propId, codeId: propCodeId }) {
  const { id: paramId } = useParams();
  const id = propId || paramId;
  const [searchParams] = useSearchParams();
  const currentPage = searchParams.get('page') || 1;
  // codeId 우선순위: prop → searchParams → post.code_id
  const [codeId, setCodeId] = useState(propCodeId || searchParams.get('code_id'));

  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [boardName, setBoardName] = useState('');
  const [loading, setLoading] = useState(true);
  const [showTodoLayer, setShowTodoLayer] = useState(false);
  const todoRegBtnRef = useRef(null);
  const [fixedAiResultEl, setFixedAiResultEl] = useState(null);
  const { token, user } = useContext(AuthContext);

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    const ampm = hours < 12 ? '오전' : '오후';
    const hour12 = hours % 12 || 12;
    return `${year}-${month}-${day} ${ampm} ${hour12}:${minutes}:${seconds}`;
  };

  useEffect(() => {
    const fetchPostAndComments = async () => {
      try {
        const postData = await getPostById(id);
        console.log('post.files:', postData.files);
        setPost(postData);

        let boardCodeId = codeId;
        if (!boardCodeId && postData && postData.code_id) {
          boardCodeId = postData.code_id;
          setCodeId(boardCodeId);
        }
        if (boardCodeId) {
          const boardCode = await getBoardCodeById(boardCodeId);
          setBoardName(boardCode.name);
        }
      } catch (err) {
        console.error('게시글 또는 댓글 조회 실패:', err);
        alert('게시글을 불러오지 못했습니다.');
        navigate('/board');
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchPostAndComments();
  }, [id, navigate, codeId]);

  const handleDelete = async () => {
    if (window.confirm('정말 삭제하시겠습니까?')) {
      await deletePost(id, token);
      navigate(`/board?page=${currentPage}&code_id=${codeId || (post && post.code_id) || ''}`);
    }
  };

  const handleEdit = () => {
    if (!post) return;
    if (!post.parent_id) {
      navigate(`/edit/${id}?page=${currentPage}&code_id=${codeId || (post && post.code_id) || ''}`);
    } else {
      navigate(`/response/edit/${id}?page=${currentPage}&code_id=${codeId || (post && post.code_id) || ''}`);
    }
  };

  const isOwnerOrAdmin =
    user && post && (user.role === 'admin' || user.userid === post.writer_id);

  if (loading) {
    return (
      <div className="text-center text-muted mt-5">
        <div className="spinner-border text-secondary mb-3" role="status" />
        <p>불러오는 중...</p>
      </div>
    );
  }

  return (
    <div className="container-fluid view-post">
      <div className="row">
        <div className="col-12">

          {/* ✅ 상단 고정 영역 */}
          <div className={`post-header-fixed ${collapsed ? 'collapsed' : 'expanded'}`}>
            <div>
              {boardName && (
                <small className="text-muted d-block mb-1">{boardName}</small>
              )}
              <h4 className="mb-0 text-dark">{post.title}</h4>
            </div>

            <div className="d-flex gap-2" style={{ position: 'relative' }}>
              <button
                className="btn btn-outline-primary btn-sm"
                onClick={() =>
                  navigate(`/board?page=${currentPage}&code_id=${codeId}`)
                }
              >
                목록
              </button>
              {isOwnerOrAdmin && (
                <>
                  <button
                    className="btn btn-outline-info btn-sm"
                    onClick={handleEdit}
                  >
                    수정
                  </button>
                  <button
                    className="btn btn-outline-danger btn-sm"
                    onClick={handleDelete}
                  >
                    삭제
                  </button>
                </>
              )}
              <button
                className="btn btn-outline-success btn-sm"
                onClick={() => navigate(`/response/write/${id}?page=${currentPage}&code_id=${codeId}`)}
              >
                응답 작성
              </button>
              <button
                ref={todoRegBtnRef}
                className="btn btn-outline-secondary btn-sm"
                onClick={() => setShowTodoLayer((prev) => !prev)}
              >
                To-Do 등록
              </button>
              <AiContentToolbar
                fixedSummarizeModel="bnksys/eeve:10.8b-korean-instruct-q5_k_s-v1"
                fixedTranslateModel="translategemma:4b"
                content={post?.content || ''}
                title={post?.title || ''}
                resultTarget={fixedAiResultEl}
              />
              {showTodoLayer && post && (
                <TodoRegisterLayer
                  direction="down"
                  align="right"
                  source={{
                    ref_type: TODO_REF_TYPE.BOARD,
                    ref_table: TODO_REF_TABLE.BOARD,
                    ref_id: id,
                    title: post.title,
                  }}
                  anchorRef={todoRegBtnRef}
                  onClose={() => setShowTodoLayer(false)}

                />
              )}
            </div>
          </div>
          {/* ✅ 작성자 + 작성일 */}
          <div className="d-flex justify-content-between text-muted mb-3 pt-4">
            <small>작성자: {post.writer_name}</small>
            <small>{formatDateTime(post.created_at)}</small>
          </div>
          {/* ✅ AI — 모델 선택 */}
          <div className="mb-4">
            <AiContentToolbar
              content={post?.content || ''}
              title={post?.title || ''}
              fileTextsUrl={`/api/board/post/${id}/file-texts`}
            />
          </div>
          {/* 고정 모델 AI 결과 렌더링 위치 */}
          <div ref={setFixedAiResultEl} />
          {/* ✅ 내용 */}
          <div className="post-body mb-4">
            <div
              className="post-content"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />
          </div>

          {/* ✅ 첨부파일 */}
          {post.files && post.files.length > 0 && (
            <div className="mb-4">
              <h6 className="fw-bold mb-2">첨부파일</h6>
              <FileAttachment
                files={post.files}
                mode="view"
                enableAi={true}
                fileTextApiBase="/api/board/file"
              />
            </div>

          )}

          {/* ✅ 댓글 영역 */}
          <div className="mt-4">
            <CommentList
              postId={id}
              fileTextsUrl={`/api/board/post/${id}/file-texts`}
              currentUser={user}
              token={token}
              codeId={codeId}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default ViewPost;
